'use client'
import React, { useRef, useImperativeHandle } from 'react'
import { Heading, Button, Picture, Text } from '../../components/index.js'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { useExposure } from '../../hooks/useExposure.js'
import type { ProductHeroProps } from './types.js'

const componentType = 'image'
const componentName = 'product_hero'

const ProductHero = React.forwardRef<HTMLDivElement, ProductHeroProps>(({ data, className }, ref) => {
  const { title, subtitle, ctaText, poster, mobPoster, ctaLink, theme = 'light' } = data

  const boxRef = useRef<HTMLDivElement>(null)

  useExposure(boxRef, {
    componentType,
    componentName,
    componentTitle: title,
    componentDescription: subtitle,
  })

  useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

  return (
    <section
      ref={boxRef}
      data-ui-component-id="ProductHero"
      className={cn(
        'product-hero text-info-primary tablet:flex-row tablet:gap-[16px] lg-desktop:gap-[64px] flex w-full flex-col items-center justify-between gap-[32px]',
        {
          'aiui-dark': theme === 'dark',
        },
        className
      )}
    >
      <div className="product-hero__content tablet:basis-[42%] laptop:basis-[36%] flex w-full flex-col items-start gap-[24px]">
        <div className="product-hero__text">
          <Heading as={'h3'} size={4} html={title} className="product-hero__title" />
          <Heading
            as={'h4'}
            size={2}
            html={subtitle}
            className="product-hero__subtitle product-hero__subtitle--desktop laptop:mt-[12px] laptop:block desktop:mt-[16px] mt-[4px] hidden"
          />
          <Text
            as={'p'}
            size={2}
            html={subtitle}
            className="product-hero__subtitle product-hero__subtitle--mobile laptop:mt-[12px] laptop:hidden desktop:mt-[16px] mt-[4px] block"
          />
        </div>
        {ctaText && (
          <a href={ctaLink} className="product-hero__cta-link">
            <Button className="product-hero__cta-button w-fit">{ctaText}</Button>
          </a>
        )}
      </div>
      <Picture
        className="product-hero__image tablet:aspect-[824/640] tablet:basis-[58%] laptop:basis-[64%] rounded-box aspect-[358/360] shrink"
        source={`${poster?.url}, ${mobPoster?.url} 768`}
        imgClassName="h-full object-cover"
      />
    </section>
  )
})

ProductHero.displayName = 'ProductHero'

export default withLayout(ProductHero)
